import { Alert, Box, Grid, Stack, Typography } from '@mui/material'
import {
  useImportAllIfxBankJob,
  useImportAllIfxLoans,
  useTerminateEtlBatchOrchestrator,
  useTerminateLoanValidationOrchestrator,
} from '@src/api/admin-commands-api'
import { useNotification } from '@src/providers/NotificationProvider'
import { DevToolsCommandType } from '@src/types/dev-tools'
import { UseMutationResult } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import AdminCommandCard from './AdminCommandCard'
import CleanLoansByProgramCard from './CleanLoansByProgramCard'
import ImportLoansByIdsCard from './ImportLoansByIdsCard'

type AdminCommandMutation = UseMutationResult<void, Error, void, unknown>

const DevToolsMain = () => {
  const { t } = useTranslation()
  const { showSuccess, showError } = useNotification()

  const terminateEtlBatch = useTerminateEtlBatchOrchestrator()
  const terminateLoanValidation = useTerminateLoanValidationOrchestrator()
  const importAllIfxLoans = useImportAllIfxLoans()
  const importAllIfxBankJob = useImportAllIfxBankJob()

  const runCommand = async (mutation: AdminCommandMutation) => {
    try {
      await mutation.mutateAsync()
      showSuccess(t('admin.devTools.messages.success'))
    } catch {
      showError(t('admin.devTools.messages.error'))
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle1" color="text.secondary">
            {t('admin.devTools.subtitle')}
          </Typography>
        </Box>

        <Alert
          severity="warning"
          sx={{
            fontWeight: 'bold',
            fontSize: '1.1rem',
            '& .MuiAlert-message': { flex: 1, textAlign: 'center' },
          }}
        >
          {t('admin.devTools.accessWarning')}
        </Alert>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <AdminCommandCard
              title={t('admin.devTools.commands.terminateEtlBatch.title')}
              description={t('admin.devTools.commands.terminateEtlBatch.description')}
              actionLabel={t('admin.devTools.commands.terminateEtlBatch.actionLabel')}
              confirmationMessage={t('admin.devTools.commands.terminateEtlBatch.confirmationMessage')}
              onExecute={() => runCommand(terminateEtlBatch)}
              isPending={terminateEtlBatch.isPending}
              commandType={DevToolsCommandType.TerminateEtlBatch}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <AdminCommandCard
              title={t('admin.devTools.commands.terminateLoanValidation.title')}
              description={t('admin.devTools.commands.terminateLoanValidation.description')}
              actionLabel={t('admin.devTools.commands.terminateLoanValidation.actionLabel')}
              confirmationMessage={t('admin.devTools.commands.terminateLoanValidation.confirmationMessage')}
              onExecute={() => runCommand(terminateLoanValidation)}
              isPending={terminateLoanValidation.isPending}
              commandType={DevToolsCommandType.TerminateLoanValidation}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <AdminCommandCard
              title={t('admin.devTools.commands.importAllIfxLoans.title')}
              description={t('admin.devTools.commands.importAllIfxLoans.description')}
              actionLabel={t('admin.devTools.commands.importAllIfxLoans.actionLabel')}
              confirmationMessage={t('admin.devTools.commands.importAllIfxLoans.confirmationMessage')}
              onExecute={() => runCommand(importAllIfxLoans)}
              isPending={importAllIfxLoans.isPending}
              commandType={DevToolsCommandType.ImportAllIfxLoans}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <AdminCommandCard
              title={t('admin.devTools.commands.importAllIfxBankJob.title')}
              description={t('admin.devTools.commands.importAllIfxBankJob.description')}
              actionLabel={t('admin.devTools.commands.importAllIfxBankJob.actionLabel')}
              confirmationMessage={t('admin.devTools.commands.importAllIfxBankJob.confirmationMessage')}
              onExecute={() => runCommand(importAllIfxBankJob)}
              isPending={importAllIfxBankJob.isPending}
              commandType={DevToolsCommandType.ImportAllIfxBankJob}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ImportLoansByIdsCard />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <CleanLoansByProgramCard />
          </Grid>
        </Grid>
      </Stack>
    </Box>
  )
}

export default DevToolsMain
